import type { TemplateProps } from '@/types/template';
import { cvLabel, languageLevelLabel, skillLevelLabel } from '@/i18n/cvLabels';
import { formatDateRange } from '@/utils/date';
import { fullName, isSectionEmpty, orderedSections } from '@/templates/shared/utils';
import { RichTextView } from '@/components/common/RichTextView';

export function CreativeHtml({ data, customColor, photoUrl }: TemplateProps) {
  const lang = data.settings.language;
  const accent = customColor ?? data.settings.color ?? '#7c3aed';
  const sections = orderedSections(data);
  const present = cvLabel(lang, 'present');

  return (
    <article
      className="mx-auto bg-white text-[10pt] leading-snug text-slate-900"
      style={{
        width: '210mm',
        minHeight: '297mm',
        fontFamily: 'Inter, Helvetica, Arial, sans-serif',
      }}
    >
      <header
        className="px-12 py-8 text-white"
        style={{
          background: `linear-gradient(135deg, ${accent} 0%, ${shade(accent, -20)} 100%)`,
        }}
      >
        <div className="flex items-center gap-6">
          {photoUrl && (
            <img
              src={photoUrl}
              alt=""
              className="h-28 w-28 rounded-full object-cover ring-4 ring-white/40"
            />
          )}
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">{fullName(data) || '—'}</h1>
            {data.personalInfo.title && (
              <p className="mt-1 text-base opacity-95">{data.personalInfo.title}</p>
            )}
            <ContactBar data={data} />
          </div>
        </div>
      </header>

      <div className="px-12 py-6">
        {data.personalInfo.summary && (
          <Section title={cvLabel(lang, 'summary')} accent={accent}>
            <div className="italic">
              <RichTextView markdown={data.personalInfo.summary} />
            </div>
          </Section>
        )}

        {sections.map((key) => {
          if (isSectionEmpty(data, key)) return null;
          switch (key) {
            case 'experience':
              return (
                <Section key={key} title={cvLabel(lang, 'experience')} accent={accent}>
                  {data.experience.map((e) => (
                    <div
                      key={e.id}
                      className="mb-3 border-l-4 pl-3"
                      style={{ borderColor: accent }}
                    >
                      <p className="font-semibold">{e.position}</p>
                      <p className="text-sm" style={{ color: accent }}>
                        {e.company}
                        {e.location && <span className="text-slate-500"> · {e.location}</span>}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDateRange(e.startDate, e.endDate, {
                          presentLabel: present,
                          locale: lang,
                        })}
                      </p>
                      {e.description && (
                        <div className="mt-1">
                          <RichTextView markdown={e.description} />
                        </div>
                      )}
                      {e.achievements && e.achievements.length > 0 && (
                        <ul className="mt-1 list-disc pl-5">
                          {e.achievements.map((a, i) => (
                            <li key={i}>
                              <RichTextView
                                markdown={a}
                                paragraphClassName="m-0"
                                listClassName="list-[circle] pl-5"
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </Section>
              );
            case 'education':
              return (
                <Section key={key} title={cvLabel(lang, 'education')} accent={accent}>
                  {data.education.map((e) => (
                    <div
                      key={e.id}
                      className="mb-2 border-l-4 pl-3"
                      style={{ borderColor: accent }}
                    >
                      <p className="font-semibold">
                        {e.degree}
                        {e.field && <span className="font-normal"> · {e.field}</span>}
                      </p>
                      <p className="text-sm" style={{ color: accent }}>
                        {e.institution}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatDateRange(e.startDate, e.endDate, {
                          presentLabel: present,
                          locale: lang,
                        })}
                      </p>
                      {e.description && (
                        <div className="mt-1">
                          <RichTextView markdown={e.description} />
                        </div>
                      )}
                    </div>
                  ))}
                </Section>
              );
            case 'skills':
              return (
                <Section key={key} title={cvLabel(lang, 'skills')} accent={accent}>
                  <div className="flex flex-wrap gap-1.5">
                    {data.skills.map((s) => (
                      <span
                        key={s.id}
                        className="rounded-full px-3 py-0.5 text-xs"
                        style={{
                          background: `${accent}1a`,
                          color: accent,
                          border: `1px solid ${accent}55`,
                        }}
                      >
                        {s.name}
                        {s.level && (
                          <span className="opacity-80"> · {skillLevelLabel(lang, s.level)}</span>
                        )}
                      </span>
                    ))}
                  </div>
                </Section>
              );
            case 'languages':
              return (
                <Section key={key} title={cvLabel(lang, 'languages')} accent={accent}>
                  <div className="flex flex-wrap gap-1.5">
                    {data.languages.map((l) => (
                      <span
                        key={l.id}
                        className="rounded-full px-3 py-0.5 text-xs"
                        style={{
                          background: `${accent}1a`,
                          color: accent,
                          border: `1px solid ${accent}55`,
                        }}
                      >
                        {l.name}
                        <span className="opacity-80"> · {languageLevelLabel(lang, l.level)}</span>
                      </span>
                    ))}
                  </div>
                </Section>
              );
            case 'projects':
              return (
                <Section key={key} title={cvLabel(lang, 'projects')} accent={accent}>
                  {data.projects?.map((p) => (
                    <div key={p.id} className="mb-2">
                      <p className="font-semibold">{p.name}</p>
                      {p.technologies && p.technologies.length > 0 && (
                        <p className="text-xs italic" style={{ color: accent }}>
                          {p.technologies.join(' · ')}
                        </p>
                      )}
                      {p.description && <RichTextView markdown={p.description} />}
                      {p.url && <p className="text-xs text-slate-500">{p.url}</p>}
                    </div>
                  ))}
                </Section>
              );
            case 'certifications':
              return (
                <Section key={key} title={cvLabel(lang, 'certifications')} accent={accent}>
                  <ul>
                    {data.certifications?.map((c) => (
                      <li key={c.id} className="mb-1">
                        <span className="font-semibold">{c.name}</span> — {c.issuer}
                        <span className="ml-2 text-xs text-slate-500">{c.date}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              );
            case 'customSections':
              return (
                <div key={key}>
                  {data.customSections?.map((c) => (
                    <Section key={c.id} title={c.title} accent={accent}>
                      <RichTextView markdown={c.content} />
                    </Section>
                  ))}
                </div>
              );
            default:
              return null;
          }
        })}
      </div>
    </article>
  );
}

function Section({
  title,
  accent,
  children,
}: {
  title: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-4">
      <h2
        className="mb-2 text-sm font-extrabold uppercase tracking-widest"
        style={{ color: accent }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function ContactBar({ data }: { data: TemplateProps['data'] }) {
  const { email, phone, address, websites } = data.personalInfo;
  const items: React.ReactNode[] = [];
  if (email) items.push(email);
  if (phone) items.push(phone);
  if (address?.city) items.push([address.city, address.country].filter(Boolean).join(', '));
  const sites = websites ?? [];
  if (items.length === 0 && sites.length === 0) return null;
  return (
    <>
      {items.length > 0 && (
        <p className="mt-2 text-xs opacity-95">
          {items.map((item, i) => (
            <span key={i}>
              {i > 0 && '  ·  '}
              {item}
            </span>
          ))}
        </p>
      )}
      {sites.map((w) => (
        <p key={w.id} className="mt-1 text-xs opacity-95">
          {w.label}:{' '}
          <a href={w.url} className="underline-offset-2 hover:underline">
            {w.displayText || w.url}
          </a>
        </p>
      ))}
    </>
  );
}

function shade(hex: string, percent: number): string {
  const m = hex.replace('#', '');
  if (m.length !== 6) return hex;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  const factor = 1 + percent / 100;
  const clamp = (n: number) => Math.min(255, Math.max(0, Math.round(n * factor)));
  return '#' + [clamp(r), clamp(g), clamp(b)].map((n) => n.toString(16).padStart(2, '0')).join('');
}
